// Top bar for the right-hand content area: section title, global search
// (pages + people, projects, requests, policies, documents), notifications
// bell and the user menu. Rendered inside #page-content on every page.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { wsAPI } from '../services/api';
import { canAccess, ROLE_CONFIG } from '../utils/roles';
import { NAV_PAGES, sectionFor } from '../utils/nav';

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
const ago = (d) => {
  const s = Math.max(0, (Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} d ago`;
};

function useClickAway(ref, onAway, active) {
  useEffect(() => {
    if (!active) return undefined;
    const on = (e) => { if (ref.current && !ref.current.contains(e.target)) onAway(); };
    document.addEventListener('mousedown', on);
    return () => document.removeEventListener('mousedown', on);
  }, [ref, onAway, active]);
}

export default function Topbar({ section }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const title = section || sectionFor(pathname);

  // ── Search ─────────────────────────────────────────────────────────────────
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState([]);
  const [active, setActive] = useState(0);
  const searchRef = useRef();
  useClickAway(searchRef, () => setOpen(false), open);

  const pages = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return NAV_PAGES.filter((p) => (!p.key || canAccess(user, p.key)) && `${p.label} ${p.group} ${p.keywords || ''}`.toLowerCase().includes(t))
      .slice(0, 6)
      .map((p) => ({ group: 'Pages', title: p.label, subtitle: p.group, link: p.to, icon: p.icon }));
  }, [q, user]);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRemote([]); return undefined; }
    const h = setTimeout(() => {
      wsAPI.search(t).then((d) => setRemote(d.results || [])).catch(() => setRemote([]));
    }, 280);
    return () => clearTimeout(h);
  }, [q]);

  const results = [...pages, ...remote];
  useEffect(() => { setActive(0); }, [q]);
  const go = (r) => {
    if (!r) return;
    setOpen(false);
    setQ('');
    navigate(r.link);
  };

  // ── Notifications ──────────────────────────────────────────────────────────
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [nOpen, setNOpen] = useState(false);
  const nRef = useRef();
  useClickAway(nRef, () => setNOpen(false), nOpen);
  const loadNotifs = () => wsAPI.notifications().then((d) => { setNotifs(d.notifications || []); setUnread(d.unread || 0); }).catch(() => {});
  useEffect(() => {
    if (!user) return undefined;
    loadNotifs();
    const t = setInterval(() => { if (document.visibilityState === 'visible') loadNotifs(); }, 60000);
    return () => clearInterval(t);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNotif = async (n) => {
    setNOpen(false);
    if (!n.read) { wsAPI.readNotification(n._id).catch(() => {}); setUnread((u) => Math.max(0, u - 1)); setNotifs((l) => l.map((x) => (x._id === n._id ? { ...x, read: true } : x))); }
    if (n.link) navigate(n.link);
  };
  const readAll = () => { wsAPI.readAllNotifications().then(() => { setUnread(0); setNotifs((l) => l.map((x) => ({ ...x, read: true }))); }).catch(() => {}); };

  // ── User menu ──────────────────────────────────────────────────────────────
  const [uOpen, setUOpen] = useState(false);
  const uRef = useRef();
  useClickAway(uRef, () => setUOpen(false), uOpen);
  const roleLabel = ROLE_CONFIG[user?.role]?.label || user?.role;

  let lastGroup = null;
  return (
    <header className="tse-topbar">
      <div className="tse-topbar-title">{title}</div>

      <div className="tse-topbar-search" ref={searchRef}>
        <i className="bi bi-search" />
        <input
          value={q}
          placeholder="Search people, projects, requests, documents and more..."
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === 'Enter') go(results[active]);
            if (e.key === 'Escape') setOpen(false);
          }}
          aria-label="Search workspace"
        />
        {open && q.trim() && (
          <div className="tse-dropdown tse-search-results">
            {results.length === 0 && <div className="tse-search-group" style={{ padding: 16, textTransform: 'none', fontSize: '0.85rem' }}>{q.trim().length < 2 ? 'Keep typing…' : 'No matches'}</div>}
            {results.map((r, i) => {
              const head = r.group !== lastGroup ? <div className="tse-search-group">{r.group}</div> : null;
              lastGroup = r.group;
              return (
                <React.Fragment key={`${r.group}-${i}`}>
                  {head}
                  <div className={`tse-search-item ${i === active ? 'active' : ''}`} onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); go(r); }}>
                    <i className={`bi ${r.icon || 'bi-arrow-return-right'}`} style={{ color: '#64768a' }} />
                    <div style={{ minWidth: 0 }}>
                      <div className="t">{r.title}</div>
                      {r.subtitle && <div className="s">{r.subtitle}</div>}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      <div className="tse-topbar-actions">
        <div style={{ position: 'relative' }} ref={nRef}>
          <button type="button" className="tse-icon-btn" onClick={() => { setNOpen((o) => !o); if (!nOpen) loadNotifs(); }} aria-label="Notifications">
            <i className="bi bi-bell" />
            {unread > 0 && <span className="tse-dot">{unread > 9 ? '9+' : unread}</span>}
          </button>
          {nOpen && (
            <div className="tse-dropdown tse-notif">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e1e9f1' }}>
                <strong style={{ fontSize: '0.95rem' }}>Notifications</strong>
                {unread > 0 && <button type="button" className="tse-link" onClick={readAll}>Mark all read</button>}
              </div>
              {notifs.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#64768a', fontSize: '0.88rem' }}>You're all caught up</div>}
              {notifs.map((n) => (
                <div key={n._id} className={`tse-notif-item ${n.read ? '' : 'unread'}`} onClick={() => openNotif(n)}>
                  <div className="t">{n.title}</div>
                  {n.body && <div className="b">{n.body}</div>}
                  <div className="d">{n.module ? `${n.module} · ` : ''}{ago(n.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tse-user" ref={uRef} onClick={() => setUOpen((o) => !o)} role="button" tabIndex={0}>
          <span className="tse-avatar">{user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(user?.userName)}</span>
          <div className="who">
            <div className="n">{user?.userName || 'User'}</div>
            <div className="r">{[user?.designation || roleLabel, user?.businessUnit || user?.department].filter(Boolean).join(' · ')}</div>
          </div>
          <i className="bi bi-chevron-down" style={{ fontSize: '0.8rem', color: '#33475b' }} />
          {uOpen && (
            <div className="tse-dropdown tse-user-menu" onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '8px 16px 10px', borderBottom: '1px solid #e1e9f1', marginBottom: 4 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{user?.userName}</div>
                <div style={{ fontSize: '0.78rem', color: '#64768a' }}>{user?.email}</div>
                <div style={{ fontSize: '0.74rem', color: '#1479bd', marginTop: 2 }}>{roleLabel}{user?.employeeCode ? ` · ${user.employeeCode}` : ''}</div>
              </div>
              <Link to="/profile" onClick={() => setUOpen(false)}><i className="bi bi-person" />My Profile</Link>
              <Link to="/approvals" onClick={() => setUOpen(false)}><i className="bi bi-check2-square" />My Approvals</Link>
              <Link to="/requests/my" onClick={() => setUOpen(false)}><i className="bi bi-list-check" />My Requests</Link>
              <Link to="/policies" onClick={() => setUOpen(false)}><i className="bi bi-journal-text" />Policy Center</Link>
              <button type="button" onClick={() => { logout(); navigate('/'); }} style={{ color: '#c0392b', borderTop: '1px solid #e1e9f1', marginTop: 4 }}><i className="bi bi-box-arrow-right" />Log out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
